export const THEME_STORAGE_KEY = 'theme-preference';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export function saveThemeToStorage(theme: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Silently fail if localStorage is not available
  }
}

export function loadThemeFromStorage(): ThemePreference {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
  } catch {
    // Silently fail if localStorage is not available
  }
  return 'system';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'light' || preference === 'dark') {
    return preference;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyThemeToDOM(resolved: ResolvedTheme): void {
  if (resolved === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Bootstraps the theme on the `<html>` element by reading the user's saved
 * preference (or OS preference) and applying the `.dark` class.
 *
 * This is the same logic used by the inline FOUC-prevention script in
 * `index.html` (see that file for the inlined copy). Exposed here so it can
 * be unit-tested and reused on public routes where Redux is not available.
 *
 * Safe to call multiple times; idempotent.
 */
export function bootstrapTheme(): void {
  const pref = loadThemeFromStorage();
  applyThemeToDOM(resolveTheme(pref));
}
